// View/camera state: which fractal is selected, where the camera is
// centered, how zoomed in, and any fractal-specific extra params (Julia's c,
// Multibrot's degree). Pure data + math — no DOM, so this module behaves the
// same whether app.mjs is driving it or a test calls it directly.

import { FORMULAS } from "../fractals/formulas.mjs";

/** @type {{ formulaName: string, centerRe: number, centerIm: number, scale: number, maxIter: number, extra: Record<string, number>, precisionF64: boolean }} */
export const state = {
  formulaName: null,
  centerRe: 0,
  centerIm: 0,
  scale: 1,
  maxIter: 0,
  extra: {},
  // Render the formula's f64Variant instead, if it has one — not reset by resetView() (see activeShaderFormula()).
  precisionF64: false,
};

/** Resets `state` to the given formula's defaults, discarding any prior view/params. */
export function resetView(formulaName) {
  const formula = FORMULAS[formulaName];
  state.formulaName = formulaName;
  state.centerRe = formula.defaultView.centerRe;
  state.centerIm = formula.defaultView.centerIm;
  state.scale = formula.defaultView.scale;
  state.maxIter = formula.defaultMaxIter;
  state.extra = Object.fromEntries(formula.extraParams.map((p) => [p.name, p.default]));
}

/**
 * Resolves which FORMULAS key to actually render with: the current
 * formula's f64Variant when the precision toggle is on and one exists,
 * otherwise the formula itself. `state.formulaName` stays the "real"
 * selection throughout — this is only consulted at render time.
 * @returns {string}
 */
export function activeShaderFormula() {
  const formula = FORMULAS[state.formulaName];
  return state.precisionF64 && formula.f64Variant ? formula.f64Variant : state.formulaName;
}

/** Maps a canvas pixel to the complex point it currently represents. */
export function pixelToComplex(width, height, px, py) {
  const halfW = width * 0.5;
  const halfH = height * 0.5;
  return {
    re: state.centerRe + (px - halfW) * state.scale,
    im: state.centerIm - (py - halfH) * state.scale,
  };
}

/** Zooms by `factor`, keeping the complex point under pixel (px, py) fixed on screen. */
export function zoomAt(width, height, px, py, factor) {
  const before = pixelToComplex(width, height, px, py);
  state.scale *= factor;
  const halfW = width * 0.5;
  const halfH = height * 0.5;
  state.centerRe = before.re - (px - halfW) * state.scale;
  state.centerIm = before.im + (py - halfH) * state.scale;
}

/** Pans the view by a screen-pixel delta. */
export function panBy(dxPixels, dyPixels) {
  state.centerRe -= dxPixels * state.scale;
  state.centerIm += dyPixels * state.scale;
}
